import { Injectable, Injector } from '@angular/core';
import { SubscriberService } from './subscriber.service';
import { StorageService } from '../data/storage.service';
import { Order } from 'src/app/interfaces/orders';
import { hasOrderChanged } from 'src/app/interfaces/order-compare';
import { DateTime } from 'luxon';
import { v4 as uuidv4 } from 'uuid';
import { LoginService } from '../auth/login.service';
// Nur als Typ importiert (kein Laufzeit-Modul-Import) — vermeidet einen zirkulären Import
// (DataServerService -> ApiService -> OfflineService). Instanz wird lazy per Injector geholt.
import type { DataServerService } from '../api/data-server.service';

@Injectable({
  providedIn: 'root',
})
export class OfflineService {
  constructor(
    private sub: SubscriberService,
    private storage: StorageService,
    private login: LoginService,
    private injector: Injector,
  ) {}

  async createOfflineOrder(setAsCurrent: boolean = false) {
    const uuid = uuidv4();
    const dt = DateTime.now();
    const timestamp = dt.toISO();

    let owner = "NEW"

    let neworder: Order = {
      orderId: uuid,
      owner: owner ,
      orderTitle: 'Neuer Auftrag vom ' + dt.toLocaleString(DateTime.DATE_FULL),
      created: timestamp,
      updated: timestamp,
      status: 0,
      note: '',
      customerBase: null,
      adminEmail: '',
      customerEmail: '',
      productHistory: null,
      products: null,
      customerAddress: null,
      Id: null,
    };

    await this.createOrupdateOfflineOrder(neworder, false);

    if (setAsCurrent) {
      this.setCurrentOrder(neworder);
    }

    // Best-effort: sofort versuchen, die Order auf dem Server anzulegen (echte orderNumber/owner),
    // ohne die UI zu blockieren. Schlägt es fehl (kein Netz, Timeout, Serverfehler), bleibt der
    // Auftrag unverändert im rein-lokalen Pfad (owner: "NEW", offlineOrders) — kein Datenverlust.
    void this.tryCreateOrderOnServerImmediately(neworder, setAsCurrent);
  }

  private async tryCreateOrderOnServerImmediately(neworder: Order, wasSetAsCurrent: boolean): Promise<void> {
    try {
      // Lazy per Injector geladen, um einen zirkulären Modul-Import zu vermeiden
      // (DataServerService -> ApiService -> OfflineService).
      const dataServer = this.injector.get((await import('../api/data-server.service')).DataServerService);

      // Nur die Felder senden, die zum Anlegen nötig sind. Insbesondere `products` bewusst
      // NICHT mitschicken: Dieser Request kann durch den Timeout unten verwaisen und
      // beliebig verspätet auf dem Server einschlagen. Enthielte er dann ein leeres
      // products, würde er eine inzwischen befüllte Order auf dem Server leeren — genau
      // der Ablauf, der in Produktion ganze Produktlisten gekostet hat.
      const { products: _products, productsOriginal: _productsOriginal, ...creationPayload } =
        neworder as Order & { productsOriginal?: unknown };

      const timeoutMs = 5000;
      const upsert = await Promise.race([
        dataServer.upsertOrder(creationPayload as Order, 'createImmediate'),
        new Promise<{ status: number }>((resolve) => setTimeout(() => resolve({ status: 0 }), timeoutMs)),
      ]);

      if (upsert.status !== 200 && upsert.status !== 201) {
        return; // bleibt im rein-lokalen Pfad, wird vom bestehenden Retry-Mechanismus nachgeholt
      }

      const serverOrder = (upsert as any).data?.order;
      if (!serverOrder) return;

      // WICHTIG: Hier darf KEIN neues Order-Objekt erzeugt und über den laufenden Stand
      // gelegt werden. Zwischen dem Absenden und der Antwort (bis zu 5s) kann der Nutzer
      // weitergearbeitet haben. Ein aus einem Snapshot neu gebautes Objekt wäre eine
      // zweite, veraltete Kopie derselben Order — genau das hat in Produktion zu
      // oszillierenden `created`-Werten, verschwundenen Produkten und zurückgesetzten
      // Kundendaten geführt.
      //
      // Stattdessen werden ausschließlich die beiden Felder nachgetragen, die der Server
      // vergeben hat, und zwar direkt auf den jeweils AKTUELLEN Objekten.
      const applyServerFields = (target: Order | null | undefined): void => {
        if (!target || target.orderId !== neworder.orderId) return;
        target.owner = serverOrder.owner;
        target.orderNumber = serverOrder.orderNumber;
        // Revision mitnehmen, sonst wertet der Server den nächsten Save als Konflikt.
        if (typeof serverOrder.revision === 'number') {
          target.revision = serverOrder.revision;
        }
      };

      applyServerFields(this.sub.currentOrder.getValue());
      const currentOffline = this.sub.offlineOrders.getValue().find(o => o.orderId === neworder.orderId);
      applyServerFields(currentOffline);
      applyServerFields(neworder);

      // Den aktuellen Stand als synchronisiert markieren — nie eine rekonstruierte Kopie.
      const authoritative = currentOffline ?? this.sub.currentOrder.getValue() ?? neworder;
      await this.markOfflineOrderSynced(authoritative);

      // BEWUSST KEINE Sync-Basis setzen: Bei diesem Aufruf wurde `products` absichtlich
      // nicht mitgeschickt. Eine Basis daraus würde behaupten, der Server kenne die
      // Produkte bereits — der nächste Teil-Update-Diff würde sie dann auslassen und sie
      // kämen nie auf dem Server an. Ohne Basis sendet der erste reguläre Save alles.

      if (wasSetAsCurrent && this.sub.currentOrder.getValue()?.orderId === neworder.orderId) {
        // Nur ein Change-Detection-Signal, kein Ersetzen des Objekts.
        this.sub.currentOrdertNext(this.sub.currentOrder.getValue());
      }
    } catch (error) {
      console.error('Sofort-Erstellung auf dem Server fehlgeschlagen, bleibt lokal:', error);
    }
  }

  async loadOfflineOrders() {
    const loaded = await this.storage.getData('offlineOrders');
    this.sub.offlineOrdersNext(loaded || []);
    await this.restoreCurrentOrder(loaded || []);

    const synced = await this.storage.getData('syncedOfflineOrders');
    this.sub.syncedOfflineOrdersNext(synced || []);
  }

  private async restoreCurrentOrder(orders: Order[]) {
    const savedId = await this.storage.getData<string>('currentOrderId');
    if (!savedId) return;
    const order = orders.find(o => o.orderId === savedId);
    if (order) {
      this.sub.currentOrdertNext(order);
    } else {
      await this.storage.removeData('currentOrderId');
    }
  }

  async saveOfflineOrders(): Promise<boolean> {
    try {
      const orders = this.sub.offlineOrders.getValue();
      // Always save, even if empty array (to clear storage)
      await this.storage.setData('offlineOrders', orders || []);
      return true;
    } catch (error) {
      console.error('Failed to save offline orders:', error);
      return false;
    }
  }

  async createOrupdateOfflineOrder(
    order: Order,
    updateTimestamp: boolean = true
  ) {
    const openOrders = this.sub.offlineOrders.getValue();
    const isOpen = openOrders.some((x) => x.orderId === order.orderId);

    // Vergleich gegen die zuletzt PERSISTIERTE Momentaufnahme, nicht gegen den Eintrag in
    // den BehaviorSubjects: Aufrufer wie finishOrder() mutieren das Order-Objekt direkt
    // (order.state = 1) und übergeben dieselbe Referenz, die auch in der Liste steht.
    // Ein Vergleich gegen die Liste würde die Änderung deshalb nie sehen.
    const previous = await this.findPersistedOrder(order.orderId);

    // Nur bei echter inhaltlicher Änderung als "ungesynct" markieren und den Timestamp
    // erhöhen. Ohne diese Prüfung erzeugte jedes reine Öffnen/Verlassen eines Auftrags
    // einen Sync-Bedarf und damit einen überflüssigen Volldokument-Save auf dem Server.
    //
    // Liefert der Storage-Treiber ausnahmsweise dieselbe Referenz statt einer Kopie
    // (In-Memory-Fallback), wäre der Vergleich wertlos — dann sicherheitshalber als
    // geändert behandeln, damit nie ein Save verloren geht.
    const changed = !previous || previous === order || hasOrderChanged(previous, order);

    if (!changed) {
      // Nichts Fachliches geändert. Eine bereits synchronisierte Order darf dadurch nicht
      // erneut als offener Auftrag auftauchen — in dem Fall bleibt die Liste unberührt.
      if (!isOpen) {
        return;
      }
      order.sync = previous.sync;
      order.updated = previous.updated;
    } else {
      order.sync = false;

      if (updateTimestamp) {
        const dt = DateTime.now();
        const timestamp = dt.toISO();
        order.updated = timestamp;
      }
    }

    // Aktualisiere die Liste und speichere sie
    await this.sub.offlineOrdersNext([
      ...openOrders.filter((x) => x.orderId !== order.orderId),
      order,
    ]);

    await this.saveOfflineOrders();
  }

  /**
   * Liest den zuletzt PERSISTIERTEN Stand einer Order aus dem Storage — erst bei den
   * offenen (offlineOrders), dann bei den synchronisierten (syncedOfflineOrders).
   *
   * Bewusst aus dem Storage und nicht aus den BehaviorSubjects: Dort liegen dieselben
   * Objektreferenzen, die Aufrufer direkt mutieren (z. B. `order.state = 1` in
   * finishOrder()). Ein Vergleich gegen diese Referenzen wäre immer ein Vergleich des
   * Objekts mit sich selbst und würde jede Änderung übersehen.
   */
  /**
   * Speichert den zuletzt vom Server bestätigten VOLLSTÄNDIGEN Auftragsstand als
   * Vergleichsbasis für Teil-Updates. Nur so lässt sich beim nächsten Speichern
   * ermitteln, was sich tatsächlich geändert hat.
   *
   * Bewusst eine eigene, vollständige Kopie und keine Teilmenge: Geht die Basis verloren
   * oder ist sie unvollständig, wird beim nächsten Speichern wieder alles gesendet — das
   * ist der sichere Rückfall, kostet aber nie Daten.
   */
  async setSyncBaseline(order: Order): Promise<void> {
    if (!order?.orderId) return;
    const all = (await this.storage.getData<Record<string, Order>>('syncBaselines')) ?? {};
    all[order.orderId] = JSON.parse(JSON.stringify(order));
    await this.storage.setData('syncBaselines', all);
  }

  /** Liest die Vergleichsbasis für Teil-Updates. Fehlt sie, wird alles gesendet. */
  async getSyncBaseline(orderId: string | undefined): Promise<Order | undefined> {
    if (!orderId) return undefined;
    const all = await this.storage.getData<Record<string, Order>>('syncBaselines');
    return all?.[orderId];
  }

  async findPersistedOrder(orderId: string | undefined): Promise<Order | undefined> {
    if (!orderId) return undefined;

    const open = await this.storage.getData<Order[]>('offlineOrders');
    const fromOpen = open?.find((x) => x.orderId === orderId);
    if (fromOpen) return fromOpen;

    const synced = await this.storage.getData<Order[]>('syncedOfflineOrders');
    return synced?.find((x) => x.orderId === orderId);
  }

  /**
   * Verschiebt eine Order von offlineOrders nach syncedOfflineOrders, nachdem der Server
   * einen erfolgreichen Upsert bestätigt hat. Löscht nie — nur Verschieben. Endgültiges
   * Entfernen aus syncedOfflineOrders passiert ausschließlich über eine explizite
   * Nutzeraktion (Button), niemals automatisch.
   */
  async markOfflineOrderSynced(order: Order): Promise<void> {
    const currentOffline = this.sub.offlineOrders.getValue();
    const updatedOffline = currentOffline.filter((x) => x.orderId !== order.orderId);
    await this.sub.offlineOrdersNext(updatedOffline);
    await this.saveOfflineOrders();

    const currentSynced = this.sub.syncedOfflineOrders.getValue();
    const syncedOrder = { ...order, sync: true };
    const updatedSynced = [
      ...currentSynced.filter((x) => x.orderId !== order.orderId),
      syncedOrder,
    ];
    this.sub.syncedOfflineOrdersNext(updatedSynced);
    await this.storage.setData('syncedOfflineOrders', updatedSynced);

    // Jeder erfolgreiche Sync-Zeitpunkt bleibt als eigener Verlaufseintrag erhalten —
    // sonst gäbe es für bereits gesendete Aufträge keine Historie im "Alle Speicherungen"-Verlauf.
    await this.saveOrderRevision(syncedOrder);
  }

  /** Endgültiges Entfernen aus syncedOfflineOrders — nur über explizite Nutzeraktion aufrufen. */
  async deleteSyncedOfflineOrder(order: Order): Promise<boolean> {
    if (!order?.orderId) return false;
    const current = this.sub.syncedOfflineOrders.getValue();
    const updated = current.filter((x) => x.orderId !== order.orderId);
    if (updated.length === current.length) return false;
    this.sub.syncedOfflineOrdersNext(updated);
    await this.storage.setData('syncedOfflineOrders', updated);
    return true;
  }

  /** Löscht alle syncedOfflineOrders auf einmal — nur über explizite Nutzeraktion aufrufen. */
  async clearSyncedOfflineOrders(): Promise<void> {
    this.sub.syncedOfflineOrdersNext([]);
    await this.storage.setData('syncedOfflineOrders', []);
  }

  /**
   * Behält nur die `keepLatest` neuesten syncedOfflineOrders (nach `updated` sortiert),
   * löscht den Rest endgültig. Nur über explizite Nutzeraktion (mit Bestätigung) aufrufen —
   * kein automatischer Trigger, konsistent mit der "nie automatisch löschen"-Regel.
   */
  async pruneOldSyncedOrders(keepLatest: number = 500): Promise<number> {
    const current = this.sub.syncedOfflineOrders.getValue();
    if (current.length <= keepLatest) return 0;

    const sorted = [...current].sort(
      (a, b) => new Date(b.updated).getTime() - new Date(a.updated).getTime()
    );
    const kept = sorted.slice(0, keepLatest);
    const removedCount = current.length - kept.length;

    this.sub.syncedOfflineOrdersNext(kept);
    await this.storage.setData('syncedOfflineOrders', kept);

    return removedCount;
  }

  /**
   * Erstellt aus einem historischen Order-Snapshot (Revision oder Version) einen komplett
   * neuen Auftrag mit neuer orderId — im Unterschied zu restoreDeletedOrder() (behält die
   * orderId) wird hier bewusst eine neue Identität vergeben, da der Snapshot als Vorlage
   * für einen neuen Auftrag dient, nicht als Wiederherstellung des Originals.
   */
  async restoreVersionAsNewOrder(snapshot: Order): Promise<Order> {
    const uuid = uuidv4();
    const dt = DateTime.now();
    const timestamp = dt.toISO();

    // state und productsOriginal gehören zum Abschluss des Originals und dürfen
    // nicht mitwandern — sonst wäre die Kopie sofort wieder abgeschlossen und
    // für den neuen Owner nicht bearbeitbar.
    const {
      orderId, Id, orderNumber, owner, karmaCartId, karmaCartSentAt,
      state, productsOriginal, ...rest
    } = snapshot as any;

    const restoredOrder: Order = {
      ...rest,
      orderId: uuid,
      Id: null,
      owner: 'NEW',
      state: 0,
      created: timestamp,
      updated: timestamp,
      sync: false,
    };

    await this.createOrupdateOfflineOrder(restoredOrder, false);
    return restoredOrder;
  }

  async deleteOfflineOrder(order: Order): Promise<boolean> {
    if (!order || !order.orderId) {
      console.warn('Cannot delete offline order: Invalid order provided');
      return false;
    }

    const currentOrders = this.sub.offlineOrders.getValue();
    const orderExists = currentOrders.some((x) => x.orderId === order.orderId);

    if (!orderExists) {
      return false;
    }

    // Backup the order before deletion
    await this.backupDeletedOrder(order);

    const updatedOrders = currentOrders.filter((x) => x.orderId !== order.orderId);
    await this.sub.offlineOrdersNext(updatedOrders);
    await this.saveOfflineOrders();

    return true;
  }

  private async backupDeletedOrder(order: Order): Promise<void> {
    try {
      // Load existing deleted orders backup
      let deletedOrders: Order[] = await this.storage.getData('deletedOrdersBackup') || [];

      // Add deletion timestamp to the order
      const orderWithDeletionTime = {
        ...order,
        deletedAt: DateTime.now().toISO()
      };

      // Add to the beginning of the array
      deletedOrders.unshift(orderWithDeletionTime);

      // Keep only the last 200 deleted orders
      if (deletedOrders.length > 500) {
        deletedOrders = deletedOrders.slice(0, 500);
      }

      // Save the backup
      await this.storage.setData('deletedOrdersBackup', deletedOrders);
    } catch (error) {
      console.error('Failed to backup deleted order:', error);
      // Don't throw - deletion should continue even if backup fails
    }
  }

  async getDeletedOrdersBackup(): Promise<Order[]> {
    try {
      const deletedOrders = await this.storage.getData('deletedOrdersBackup');
      return deletedOrders || [];
    } catch (error) {
      console.error('Failed to load deleted orders backup:', error);
      return [];
    }
  }

  async restoreDeletedOrder(orderId: string): Promise<boolean> {
    try {
      const deletedOrders: Order[] = await this.storage.getData('deletedOrdersBackup') || [];
      const orderToRestore = deletedOrders.find(o => o.orderId === orderId);

      if (!orderToRestore) {
        console.warn(`Order ${orderId} not found in backup`);
        return false;
      }

      // Remove deletedAt timestamp
      const { deletedAt, ...restoredOrder } = orderToRestore as any;

      // Add back to offline orders
      await this.createOrupdateOfflineOrder(restoredOrder as Order, false);

      // Remove from deleted backup
      const updatedDeletedOrders = deletedOrders.filter(o => o.orderId !== orderId);
      await this.storage.setData('deletedOrdersBackup', updatedDeletedOrders);

      return true;
    } catch (error) {
      console.error('Failed to restore deleted order:', error);
      return false;
    }
  }

  // Revisionen — jeder inhaltlich neue Speicherstand bleibt dauerhaft erhalten, kein
  // automatisches Limit/Löschen. Endgültiges Entfernen nur über clearOrderRevisions(),
  // ausschließlich per expliziter Nutzeraktion.
  async saveOrderRevision(order: Order): Promise<void> {
    if (!order?.orderId) return;
    try {
      const key = `orderRevisions_${order.orderId}`;
      const existing: { timestamp: number; order: Order }[] = await this.storage.getData(key) || [];

      // Kein neuer Eintrag, wenn der Inhalt identisch zum letzten Stand ist (z. B. mehrfaches
      // Speichern ohne Änderung) — verhindert unnötiges Anwachsen des Verlaufs.
      if (existing.length > 0 && this.isSameOrderContent(existing[0].order, order)) {
        return;
      }

      existing.unshift({ timestamp: Date.now(), order: structuredClone(order) });
      await this.storage.setData(key, existing);
    } catch (error) {
      console.error('Failed to save order revision:', error);
    }
  }

  /** Vergleicht zwei Order-Stände inhaltlich, ohne reine Metafelder (updated/sync), die sich
   * bei jedem Speichern ändern, auch wenn der eigentliche Auftragsinhalt gleich geblieben ist. */
  private isSameOrderContent(a: Order, b: Order): boolean {
    const strip = (o: Order) => {
      const { updated, sync, ...rest } = o as any;
      return rest;
    };
    return JSON.stringify(strip(a)) === JSON.stringify(strip(b));
  }

  async getOrderRevisions(orderId: string): Promise<{ timestamp: number; order: Order }[]> {
    try {
      return await this.storage.getData(`orderRevisions_${orderId}`) || [];
    } catch {
      return [];
    }
  }

  async clearOrderRevisions(orderId: string): Promise<void> {
    try {
      await this.storage.setData(`orderRevisions_${orderId}`, []);
    } catch (error) {
      console.error('Failed to clear order revisions:', error);
    }
  }

  // Current Order
  async setCurrentOrder(
    order: Order,
    saveData: boolean = false,
    updateTimestamp: boolean = false
  ) {
    this.sub.currentOrdertNext(order);
    await this.storage.setData('currentOrderId', order.orderId);
    if (saveData) {
      this.createOrupdateOfflineOrder(order, updateTimestamp);
    }
  }

  async resetCurrentOrder(remove: boolean = false) {
    const order = await this.sub.currentOrder.getValue();
    this.sub.currentOrdertNext(null);
    await this.storage.removeData('currentOrderId');
    if (remove) {
      this.deleteOfflineOrder(order);
    }
  }
}
